import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Truck, Loader2, ExternalLink } from 'lucide-react';
import { useKeycloak } from '../auth/keycloakProvider';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { WalletSetup } from '../components/wallet/WalletSetup';

export default function Login() {
  const [isLoading, setIsLoading] = useState(false);
  
  const { 
    isAuthenticated, 
    loading, 
    requiresWalletSetup, 
    login,
    isSystemAdmin,
  } = useKeycloak();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const from = (location.state as any)?.from?.pathname || (isSystemAdmin ? '/dashboard' : '/ecmrs');

  // Track if we've already shown success toast to avoid duplicates
  const [hasNavigated, setHasNavigated] = useState(false);

  // Navigate after successful authentication
  useEffect(() => {
    if (!loading && isAuthenticated && !requiresWalletSetup && !hasNavigated) {
      setHasNavigated(true);
      toast({
        title: 'Login bem sucedido',
        description: 'Bem-vindo ao sistema eCMR.',
      });
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, loading, requiresWalletSetup, hasNavigated, navigate, from, toast]);

  const handleKeycloakLogin = () => {
    setIsLoading(true);
    login();
  };

  const handleWalletSetupComplete = () => {
    toast({
      title: 'Wallet configurada com sucesso',
      description: 'Bem-vindo ao sistema eCMR.',
    });
    navigate(from, { replace: true });
  };

  // Show wallet setup if required
  if (requiresWalletSetup) {
    return <WalletSetup onComplete={handleWalletSetupComplete} />;
  }

  // Show loading while Keycloak initializes
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
        <div className="text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary mb-4 shadow-elevated mx-auto">
            <Truck className="h-8 w-8 text-primary-foreground" />
          </div>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">A inicializar autenticação...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
      <div className="w-full max-w-md animate-scale-in">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary mb-4 shadow-elevated">
            <Truck className="h-8 w-8 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">eCMR Manager</h1>
          <p className="text-muted-foreground">Gestão de Documentos de Transporte</p>
        </div>

        <Card className="shadow-elevated">
          <CardHeader className="text-center pb-2">
            <CardTitle>Entrar na sua conta</CardTitle>
            <CardDescription>
              Clique no botão abaixo para autenticar via Keycloak
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4 mb-6">
                <p className="text-sm text-center text-muted-foreground">
                  Este sistema utiliza autenticação centralizada via Keycloak. 
                  Será redirecionado para fazer login de forma segura.
                </p>
              </div>

              <Button 
                onClick={handleKeycloakLogin} 
                className="w-full" 
                size="lg"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    A redirecionar...
                  </>
                ) : (
                  <>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Entrar com Keycloak
                  </>
                )}
              </Button>

              <p className="mt-6 text-xs text-center text-muted-foreground">
                Autenticação segura e centralizada
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
